import { Alert, Button } from "@mui/material"
import { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { toast } from "react-toastify"
import { useRecoilValue, useResetRecoilState, useRecoilState, useRecoilCallback } from "recoil"
import { MerSETALAIService } from "../../../../../Component/Api/merSETA/LAI/MerSETALAIService"
import { ConfirmationDialog } from "../../../../../Component/Common/Components/ConfirmationDialog"
import { DataTable } from "../../../../../Component/Common/Components/DataTable/DataTable"
import { FilterType, IFilter } from "../../../../../Component/Common/Components/DataTable/Filters"
import { ColumnSortOption } from "../../../../../Component/Model/Dto/ColumnSortOption"
import { filterColumnValues } from "../../../../../Component/Model/Dto/ScorecardLicense"
import { MersetaLAISearchRequest, MersetaLai } from "../../../../../Component/Model/Dto/WSP/MerSETALAI"
import { EntriesPerPage } from "../../../../../Component/Model/Table/Table"
import { defaultObject, isEmptyString } from "../../../../../Helpers/typescriptHelper"
import { OrganisationAtom } from "../../../../../State/Organisation.Atom"
import { TrainingImportButtons } from "../Import/TrainingImportButtons"
import { OrganisationLAITrainingDataTable } from "./OrganisationLAITrainingDataTable"
import { InterventionAtom } from "../Intervention.Atom"
import { DataImportType } from "../../../../../Component/Model/Enum/BEE123Enums"

export const OrganisationLAITraining = () => {
  const peopleAndTrainingOrganisation = useRecoilValue(OrganisationAtom);
  const history = useHistory();
  const [platformTrainingData, setPlatformTrainingData] = useState<MersetaLai[]>([]);
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [columnSortOption, setColumnSortOption] = useState<ColumnSortOption>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [interventionTypeId, setInterventionTypeId] = useState<number[]>();
  const [interventionTitleId, setInterventionTitleId] = useState<number[]>();
  const [beeCategoryId, setBeeCategoryId] = useState<number[]>();
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [hasNoSeta, setHasNoSeta] = useState<boolean>(false);
  const [interventionTitle, setInterventionTitle] = useState<filterColumnValues[]>(defaultObject);
  const [interventionType, setInterventionType] = useState<filterColumnValues[]>(defaultObject);
  const [beeCategory, setBeeCategory] = useState<filterColumnValues[]>(defaultObject);
  const [searchValue, setSearchValue] = useState<string>();
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const [selectedMerSETALAIId, setSelectedMerSETALAIId] = useState<number>();
  const [selectedCourseName, setSelectedCourseName] = useState<string>();
  const confirmDeleteMessage = `Are you sure you want to delete the course ${selectedCourseName}?`;

  const resetSdiAtom = useResetRecoilState(InterventionAtom);

  const setSelectedLAI = useRecoilCallback(({ set }) => (id: number) => {
    set(InterventionAtom, { id });
  });

  const trainingPeopleFilters: Array<IFilter> = [
    {
      label: 'Date',
      values: [],
      type: FilterType.dateRange, propname: 'startEndDate',
    },
    {
      label: 'BEE Categories:',
      values: beeCategory,
      type: FilterType.select, propname: 'beeCategory'
    },
    {
      label: 'Intervention Type:',
      values: interventionType,
      type: FilterType.select, propname: 'interventionTypeId'
    },
    {
      label: 'Intervention Title:',
      values: interventionTitle,
      type: FilterType.select, propname: 'interventionTitleId'
    }
  ]

  useEffect(() => {
    if (peopleAndTrainingOrganisation.OrganisationId > 0) {
      SearchTraining();
      if ((peopleAndTrainingOrganisation.SetaTypeId !== "0" || isEmptyString(peopleAndTrainingOrganisation.SetaTypeId))) {
        setHasNoSeta(true);
      }
    }
  }, [
    peopleAndTrainingOrganisation,
    entriesPerPage,
    pageNumber,
    columnSortOption,
    interventionTypeId,
    beeCategoryId,
    startDate,
    endDate,
    interventionTitleId,
    searchValue
  ]);

  useEffect(() => {
    fetchStaticData();
  }, [])

  const fetchStaticData = async () => {
    try {
      const resultTitlesPromise = await MerSETALAIService.GetMerSETAInterventionTitles();
      const resultCatgoriesPromise = await MerSETALAIService.GetMerSETACategoryInterventions();

      const [interventionTitle, beeCategories] = await Promise.all([
        resultTitlesPromise,
        resultCatgoriesPromise
      ])

      const interventionTitleData: filterColumnValues[] = interventionTitle.data.map((data) => {
        return { key: data.description, val: data.code };
      })

      const beeCategoriesData: filterColumnValues[] = beeCategories.data.map((data) => {
        return { key: data.category, val: data.id };
      })

      const interventionTypeData: filterColumnValues[] = beeCategories.data.map((data) => {
        return data.interventionTypes.map(intervention => {
          return { key: intervention.description, val: intervention.code }
        });
      }).flat();

      setInterventionTitle(interventionTitleData);
      setBeeCategory(beeCategoriesData);
      setInterventionType(interventionTypeData);


    } catch (error) {
      console.error(error);
    }
  }

  const organisationAndTrainingSearchRequest: MersetaLAISearchRequest = {
    organisationId: peopleAndTrainingOrganisation.OrganisationId,
    searchValue: searchValue,
    interventionTitleId: interventionTitleId,
    interventionTypeId: interventionTypeId,
    beeCategoryId: beeCategoryId,
    startDate: startDate,
    endDate: endDate,
    columnSortOption: columnSortOption,
    pageNumber: pageNumber,
    pageSize: entriesPerPage.defaultValue!,
    columnSortOptions: []
  }

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOption(sortOption)
    setPageNumber(1);
  };

  const searchValueChanged = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
  }

  const filtersChanged = async (filterProps: any) => {
    const { start, end } = filterProps['startEndDate'] || { start: null, end: null };
    const interventionTypeId = filterProps['interventionTypeId'] || null;
    const interventionTitleId = filterProps['interventionTitleId'] || null;
    const beeCategory = filterProps['beeCategory'] || null;

    const startDate = start ? new Date(start) : undefined;
    const endDate = end ? new Date(end) : undefined;

    setInterventionTypeId(interventionTypeId);
    setInterventionTitleId(interventionTitleId);
    setBeeCategoryId(beeCategory);
    setStartDate(startDate);
    setEndDate(endDate)
    setPageNumber(1);
  };

  const showDeleteConfirmation = (setLAIId: number, courseName: string) => {
    setSelectedMerSETALAIId(setLAIId);
    setSelectedCourseName(courseName);
    setShowConfirmationDialog(true);
  };

  const handleDelete = async () => {
    try {
      if (selectedMerSETALAIId !== undefined) {
        await MerSETALAIService.DeleteMerSETALAI(selectedMerSETALAIId);
        toast.success(`Successfully deleted MerSETA LAI`)
      }
    } catch (error) {
      console.error(error)
    } finally {
      handleCloseConfirmation();
    }
  }

  const handleCloseConfirmation = () => {
    setShowConfirmationDialog(false);
    SearchTraining();
  }

  useEffect(() => {
    resetSdiAtom();
  }, [])

  const SearchTraining = async () => {
    setIsLoading(true);
    try {
      const searchMersetaLaiService = await MerSETALAIService.SearchMerSetaLais(organisationAndTrainingSearchRequest);
      setPlatformTrainingData(searchMersetaLaiService.data.results);
      setTotalItemsCount(searchMersetaLaiService.data.totalItemsCount);
      setFilteredItemCount(searchMersetaLaiService.data.filteredItemsCount);

    } catch (error) {
      console.error(error);
      setTotalItemsCount(1);
    } finally {
      setIsLoading(false);
    }
  }

  const navigateToMerSetaLaiPage = (merSetaLaiId: number) => {
    setSelectedLAI(merSetaLaiId);
    history.push(`/organisation/training/LAI/form/Details`)
  }

  const handleRouteToAttendeeClick = (id: number) => {
    setSelectedLAI(id);
    history.push(`/organisation/training/LAI/form/Attendees`);
}

  const organisationAndTrainingTableData = OrganisationLAITrainingDataTable(platformTrainingData, showDeleteConfirmation, navigateToMerSetaLaiPage, handleRouteToAttendeeClick);

  return (
    <>
      {hasNoSeta ?
        <>
          <Button color="primary" variant="contained" onClick={() => { navigateToMerSetaLaiPage(0) }} >CAPTURE LAI</Button>
          <DataTable
            key={totalItemsCount}
            table={organisationAndTrainingTableData}
            canSearch
            entriesPerPage={entriesPerPage}
            pageNumber={pageNumber}
            filters={trainingPeopleFilters}
            filteredItemsCount={filteredItemCount}
            totalItemsCount={totalItemsCount}
            entriesPerPageChanged={entriesPerPageChanged}
            pageNumberChanged={pageNumberChanged}
            filtersChanged={filtersChanged}
            searchValueChanged={searchValueChanged}
            isLoading={isLoading}
            manualSorting={true}
            columnSortingChanged={columnSortingChanged}
            searchBoxText='Search course name...'
          />
          <TrainingImportButtons dataImportType={DataImportType.MerSetaTraining} organisationId={peopleAndTrainingOrganisation.OrganisationId} fetchData={SearchTraining}/>
        </>
        :
        <>
          <Alert severity="warning" style={{ paddingTop: 5 }}>
            In order to capture accurate training data in alignment with your SETA, please request your administrator to capture a SETA on an organisation.
          </Alert>
        </>
      }
      <ConfirmationDialog
        showDialog={showConfirmationDialog}
        handleAccept={handleDelete}
        handleCancel={handleCloseConfirmation}
        heading={"Delete Learnerships, Apprenticeships or Internships"}
        message={confirmDeleteMessage}
      />
    </>
  )
}
